/**
 * Cart upsell complementary fill.
 *
 * Liquid already rendered metafield cards then merchant leftovers. When
 * Product source is Cart items, leftover slots are filled from Shopify
 * complementary recommendations (newest cart product first). Merchant
 * slides are trimmed so the list stays at the cap.
 */
(() => {
  const TAG = 'cart-upsell';
  const ROOT = window.Shopify?.routes?.root || '/';
  const MARKER_ID = 'CartUpsellRecommendations';

  if (customElements.get(TAG)) return;

  const parseLimit = (value) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed) || parsed < 1) return 6;

    return parsed;
  };

  const parseIds = (value) =>
    String(value || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

  class CartUpsell extends HTMLElement {
    #abort = null;
    #remountTries = 0;

    connectedCallback() {
      if (this.getAttribute('data-from-cart') !== 'true') return;

      this.#fill();
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#abort = null;
    }

    async #fill() {
      const limit = parseLimit(this.getAttribute('data-limit'));

      if (this.#priorityCount() >= limit) return;

      const sectionId = document.getElementById(MARKER_ID)?.getAttribute('data-section-id');
      const productIds = parseIds(this.getAttribute('data-cart-product-ids'));

      // Tear down Splide before mutating slides so shoppers never see a
      // half-updated track while complementary fetches are in flight.
      this.#destroyCarousel();

      if (!sectionId || productIds.length === 0) {
        this.#trimToLimit(limit);
        this.#syncVisibility();
        this.#remountCarousel();
        return;
      }

      this.#abort?.abort();
      this.#abort = new AbortController();
      const { signal } = this.#abort;

      try {
        for (const productId of productIds) {
          if (this.#priorityCount() >= limit) break;

          await this.#appendRecommendations(sectionId, productId, limit, signal);
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }

      if (signal.aborted) return;

      this.#trimToLimit(limit);
      this.#syncVisibility();
      this.#remountCarousel();
    }

    async #appendRecommendations(sectionId, productId, limit, signal) {
      const params = new URLSearchParams({
        section_id: sectionId,
        product_id: productId,
        limit: String(limit),
        intent: 'complementary',
      });
      const response = await fetch(`${ROOT}recommendations/products?${params}`, { signal });

      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelectorAll('.cart-upsell__slide');
      const list = this.#list();

      if (!list) return;

      for (const slide of incoming) {
        if (this.#priorityCount() >= limit) break;

        const productIdAttr = slide.getAttribute('data-product-id');
        if (!productIdAttr || this.#hasProduct(productIdAttr)) continue;

        const next = document.importNode(slide, true);
        const merchant = list.querySelector('[data-upsell-source="merchant"]');

        if (merchant) {
          list.insertBefore(next, merchant);
        } else {
          list.append(next);
        }
      }
    }

    #list() {
      return this.querySelector('.cart-upsell__list');
    }

    #priorityCount() {
      return this.#slides('metafield').length + this.#slides('recommendation').length;
    }

    #slides(source) {
      const selector = source
        ? `.cart-upsell__slide[data-upsell-source="${source}"]`
        : '.cart-upsell__slide';

      return this.querySelectorAll(selector);
    }

    #hasProduct(productId) {
      return Boolean(this.querySelector(`.cart-upsell__slide[data-product-id="${productId}"]`));
    }

    #trimToLimit(limit) {
      const merchants = Array.from(this.#slides('merchant'));

      while (this.#slides().length > limit && merchants.length > 0) {
        merchants.pop()?.remove();
      }

      const extras = Array.from(this.#slides()).slice(limit);

      for (const slide of extras) {
        slide.remove();
      }
    }

    #syncVisibility() {
      if (this.#slides().length > 0) {
        this.removeAttribute('hidden');
        return;
      }

      this.setAttribute('hidden', '');
    }

    #destroyCarousel() {
      if (!window.themeCarousel) return;

      window.themeCarousel.destroyAll(this);
    }

    #remountCarousel() {
      const carousel = this.querySelector('.cart-upsell__carousel');

      if (!carousel || !this.isConnected) return;

      if (!window.themeCarousel) {
        if (this.#remountTries > 60) return;

        this.#remountTries += 1;
        requestAnimationFrame(() => this.#remountCarousel());
        return;
      }

      this.#destroyCarousel();
      window.themeCarousel.evaluate(carousel);
    }
  }

  customElements.define(TAG, CartUpsell);
})();
